import React, { useState, useEffect } from 'react';
import { initializePayment } from '../services/paymentService';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, serverTimestamp, getDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { FIREBASE_CONFIG } from '../config/constants';
import pricingPhilosophyImage from '../assets/pricingB.jpg';

// Custom CheckIcon component
const CheckIcon = () => (
  <svg className="h-6 w-6 flex-none text-blue-500" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z" />
  </svg>
);

const gradientBorderStyles = `
@keyframes gradientBorder {
  0% { border-image-source: linear-gradient(to right, #60A5FA, #34D399); }
  50% { border-image-source: linear-gradient(to right, #34D399, #60A5FA); }
  100% { border-image-source: linear-gradient(to right, #60A5FA, #34D399); }
}

.hover\\:border-gradient-to-r:hover {
  border-image-source: linear-gradient(to right, #60A5FA, #34D399);
  border-image-slice: 1;
  animation: gradientBorder 3s ease infinite;
}`;

const PricingTier = ({ title, price, description, features, popular, cta, user, type }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Helper function to determine if button should be disabled
  const isButtonDisabled = () => {
    if (!user) return false; // Not disabled for non-logged in users
    if (type === 'premium' && user.has_purchased_premium) return true; // Disable premium if already purchased
    if (type === 'top_up' && !user.has_purchased_premium) return true;
    return false;
  };

  // Helper function to get button text
  const getButtonText = () => {
    if (loading) return 'Processing...';
    if (!user) return cta;
    if (type === 'premium' && user.has_purchased_premium) return 'Already Premium';
    if (type === 'top_up' && !user.has_purchased_premium) return 'Purchase Premium First';
    return cta;
  };

  const handlePayment = async () => {
    if (!user) {
      alert('Please sign in to continue');
      return;
    }

    try {
      setLoading(true);
      await initializePayment(
        user.uid,
        type,
        price * 100, // Convert to paise
        user.email,
        user.displayName
      );
    } catch (error) {
      console.error('Payment error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`relative rounded-2xl p-8 transition-all duration-300 ${
      popular 
        ? 'bg-blue-600/10 border-2 border-blue-500/20' 
        : 'bg-gray-900/30 border border-gray-800/50 hover:border-purple-500/40 hover:bg-purple-600/10'
    }`}>
      {popular && (
        <div className="absolute -top-5 left-0 right-0 mx-auto w-fit px-4 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-sm font-medium text-white">
          Recommended
        </div>
      )}
      
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-100 mb-2">{title}</h3>
        <div className="flex items-baseline justify-center gap-x-2">
          <span className="text-5xl font-bold text-gray-100">₹{(price).toFixed(0)}</span>
        </div>
        <p className="text-gray-400 mt-4">{description}</p>
      </div>

      <ul className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-x-3 text-gray-300">
            <CheckIcon />
            {feature}
          </li>
        ))}
      </ul>

      <button
        onClick={handlePayment}
        disabled={isButtonDisabled() || loading}
        className={`w-full px-6 py-3 text-center text-white font-medium rounded-lg transition-all
          ${popular 
            ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90' 
            : 'bg-gradient-to-r from-purple-400/80 to-pink-400/80 hover:from-purple-400 hover:to-pink-400'
          }
          ${(isButtonDisabled() || loading) ? 'opacity-50 cursor-not-allowed hover:opacity-50' : ''}
        `}
      >
        {getButtonText()}
      </button>
    </div>
  );
};

const Pricing = ({ user }) => {
  const [userData, setUserData] = useState(null);
  
  // Auto-scroll effect
  useEffect(() => {
    const timer = setTimeout(() => {
      const plansSection = document.getElementById('pricing-plans');
      if (plansSection) {
        plansSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 5000); // 5 seconds

    return () => clearTimeout(timer);
  }, []);

  // Fetch user data including subscription status
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.uid) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Get accurate count of interviews with feedback
          try {
            const interviewsQuery = query(
              collection(db, FIREBASE_CONFIG.INTERVIEW_COLLECTION()),
              where('uid', '==', user.uid),
              where('has_feedback', '==', true)
            );
            
            const querySnapshot = await getDocs(interviewsQuery);
            const actualCompleted = querySnapshot.size;
            const actualRemaining = Math.max(0, userData.interview_quota - actualCompleted);
            
            console.log('DEBUG: Pricing - Actual counts:', {
              queryCount: actualCompleted,
              quota: userData.interview_quota,
              storedCompleted: userData.total_interviews_completed || 0,
              storedRemaining: userData.interviews_remaining || 0,
              actualRemaining: actualRemaining
            });
            
            // Make a temporary userData object with accurate counts
            setUserData({
              ...userData,
              _actualInterviewsWithFeedback: actualCompleted,
              _actualInterviewsRemaining: actualRemaining
            });
          } catch (error) {
            console.error('Error counting interviews with feedback:', error);
            setUserData(userData);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    fetchUserData();
  }, [user]);

  const tiers = [
    {
      title: 'Premium',
      price: 999,
      description: '4 High-Quality Mock Interviews',
      features: [
        'Access to both "Chill" & "Grill" type interviews',
        'Detailed Feedback & Score',
        'No two interviews will be identical',
        'Valid for 4 complete interviews - any type'
      ],
      popular: true,
      cta: 'Get Premium Access',
      type: 'premium'
    },
    {
      title: 'Top-up',
      price: 999,
      description: 'Add 4 More Interviews',
      features: [
        'Same features as Premum + Additional 4 interviews',
        'Use anytime after Premium purchase',
        'Multiple Top-ups allowed',
        'Valid for 30 days from the time of purchase'
      ],
      popular: false,
      cta: 'Purchase Top-up',
      type: 'top_up'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B0F17] py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Title for both sections */}
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent text-center mb-16">
          Pricing Philosophy
        </h1>

        {/* Philosophy Section */}
        <div className="mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[650px] rounded-2xl overflow-hidden">
              <img
                src={pricingPhilosophyImage}
                alt="Pricing Philosophy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            
            <div className="space-y-6">
              <div className="text-gray-300 text-lg leading-snug text-justify space-y-6">
                <p>
                  Our pricing is built on a simple idea: democratizing quality interview preparation.
                </p>

                <p>
                  There are three core pillars to it: Better. Faster. Cheaper.
                </p>

                <ul className="space-y-4">
                  <li className="flex gap-2">
                    <span className="text-blue-400 font-semibold">•</span>
                    <span><span className="text-blue-400">Better?</span> Most human interviewers aren't worth your time. Only the top 2% are. We're building something Turing-complete at the 98th percentile—not a run-of-the-mill chatbot, but a system that thinks, challenges, and refines like the best.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-400 font-semibold">•</span>
                    <span><span className="text-blue-400">Faster?</span> Try booking a human interview aka "mentor" for 2 PM on a Sunday. Good luck. First, pray they don’t have brunch plans. Then, hope they don’t reschedule, forget, or roll in late with a bad wi-fi connection and an even worse attitude. With us? No scheduling gymnastics. No ghosting. No back-and-forth emails. No waitlists. Just 24/7 interview accessibility and instant feedback.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-400 font-semibold">•</span>
                    <span><span className="text-blue-400">Cheaper?</span> The market is full of WAT/PI "packages" and "mentors" selling snake oil under the banner of "guaranteed success". With us, you only pay for one thing: mock interviews. Where does your money go to? LLM runtime, software infrastructure, and research. That's it. Not for fancy corporate lobbies, mahogany conference room tables, espresso machines, foosball rigs, and definitely not to fund an HR coordinator orchestrating another "team building" offsite retreat.</span>
                  </li>
                </ul>

                <p>
                  Moreover, we don't do fake urgency, behavioral tricks, or sleazy spam tactics. You pay for four mock interviews. Need more? Top up in sets of four.
                </p>

                {/* <p className="font-semibold">
                  No tricks. No dilution. No fluff.
                </p> */}
              </div>
            </div>
          </div>
        </div>

        {/* Choose Your Plan Section */}
        <div id="pricing-plans" className="text-center mb-16 scroll-mt-24">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          Pick Your Edge
          </h1>
          {/* Show remaining interviews for premium users */}
          {userData?.subscriptionStatus === 'premium' && (
            <div className="text-lg text-blue-400 mb-4">
              Current Plan: Premium (Interviews Remaining: 
              {userData._actualInterviewsRemaining !== undefined
                ? userData._actualInterviewsRemaining
                : (userData.interviews_remaining || 0)}
              )
            </div>
          )}
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Start with Premium to unlock 4 interviews. Need more practice? Add interviews anytime with Top-up.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {tiers.map((tier, index) => (
            <PricingTier 
              key={index} 
              {...tier} 
              user={userData || user} 
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Pricing; 